import "./AnalysisSelection.css";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { logout } from "../utils/api";

function AnalysisSelection() {
  const navigate = useNavigate();
  const [time, setTime] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = new Date();
      const hours = currentTime.getHours() % 12 || 12;
      const minutes = currentTime.getMinutes().toString().padStart(2, "0");
      const seconds = currentTime.getSeconds().toString().padStart(2, "0");
      const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
      setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const handleSelection = (type) => {
    // Store the analysis type so subsequent pages know the chosen path
    localStorage.setItem('analysisType', type.toLowerCase());
    // Both paths upload data next
    navigate("/auto-ml");
  };

  return (
    <div className="analysis-selection">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-content">
          <div className="nav-left-group">
            <div className="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
              <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
            </div>
            <button className="back-nav-button" onClick={() => navigate("/dashboard")}>
              Back
            </button>
          </div>
          <div className="nav-right">
            <div className="digital-clock">{time}</div>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Select Analysis Type</h1>
          <p className="header-subtitle">Choose the machine learning approach that best fits your healthcare research question</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        {/* Options */}
        <div className="selection-options">
          <div className="option-card" onClick={() => handleSelection("Supervised")}>
            <div className="option-icon icon-supervised"></div>
            <h2>Supervised Learning</h2>
            <p className="option-description">
              Predict outcomes or classify data using labeled examples. Ideal when you have a target variable to predict.
            </p>
            
            <div className="use-cases">
              <h3>Use Cases:</h3>
              <ul>
                <li>Predicting patient outcomes (survival, readmission, diagnosis)</li>
                <li>Classifying disease types or severity levels</li>
                <li>Estimating treatment effectiveness or recovery time</li>
                <li>Forecasting patient risk scores or medication dosages</li>
              </ul>
            </div>

            <div className="option-features">
              <div className="feature-tag">Classification</div>
              <div className="feature-tag">Regression</div>
              <div className="feature-tag">Prediction</div>
            </div>
          </div>

          <div className="option-card" onClick={() => handleSelection("Unsupervised")}>
            <div className="option-icon icon-unsupervised"></div>
            <h2>Unsupervised Learning</h2>
            <p className="option-description">
              Discover hidden patterns and groups in data without predefined labels. Perfect for exploratory analysis.
            </p>
            
            <div className="use-cases">
              <h3>Use Cases:</h3>
              <ul>
                <li>Identifying patient subgroups or phenotypes</li>
                <li>Finding patterns in treatment responses</li>
                <li>Detecting anomalies or outliers in clinical data</li>
                <li>Reducing dimensionality for visualization</li>
              </ul>
            </div>

            <div className="option-features">
              <div className="feature-tag">Clustering</div>
              <div className="feature-tag">Anomaly Detection</div>
              <div className="feature-tag">Pattern Discovery</div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="help-section">
          <div className="help-card">
            <h3>Not Sure Which to Choose?</h3>
            <div className="help-content">
              <div className="help-item">
                <strong>Choose Supervised Learning if:</strong>
                <ul>
                  <li>You have a specific outcome or variable you want to predict</li>
                  <li>Your dataset includes labeled examples (e.g., known diagnoses, outcomes)</li>
                  <li>You want to build a predictive model</li>
                </ul>
              </div>
              <div className="help-item">
                <strong>Choose Unsupervised Learning if:</strong>
                <ul>
                  <li>You want to explore your data without predefined labels</li>
                  <li>You're looking for hidden patterns or groupings</li>
                  <li>You want to identify similar patient groups or outliers</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalysisSelection;
